import React, { useState } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useProjectTreeStore } from 'store/projectContract.store';

import { useMutationUploadRiskRegisterCsv } from 'api/mutations/uploadRiskRegisterCsv.mutation';

import { CustomizedModalBase } from 'components/common';
import classes from 'components/widgets/RiskRegister/components/UploadRisksButton/components/UploadRisksModal/UploadRisksModal.module.scss';
import { FormContent } from 'components/widgets/RiskRegister/components/UploadRisksButton/components/UploadRisksModal/components/FormContent/FormContent';
import { PartialContent } from 'components/widgets/RiskRegister/components/UploadRisksButton/components/UploadRisksModal/components/PartialContent/PartialContent';

interface Props {
    isShowModal: boolean;
    handleModalClose: () => void;
}

export interface FormProps {
    assignee: ISelectOption<string> | null;
    tags: string[];
    file: File | null;
    contractId: number | undefined;
}

export const UploadRisksModal = ({ isShowModal, handleModalClose }: Props) => {
    const queryClient = useQueryClient();
    const { handleSnackBar } = useCustomSnackBar();

    const [partialErrorMessages, setPartialErrorMessages] = useState<string[] | null>(null);
    const mutation = useMutationUploadRiskRegisterCsv();

    const { sendEventWithDimensions } = useEventWithDimensions();

    const { contract } = useProjectTreeStore();

    const form = useForm<FormProps>({
        defaultValues: {
            assignee: undefined,
            tags: [],
            file: null,
            contractId: contract?.id,
        },
    });

    const submitForm = (data: FormProps) => {
        const { assignee, tags, file, contractId } = data;
        if (file) {
            const params = {
                assignee: assignee ? assignee.value : null,
                tags,
                file,
                contractId,
            };

            mutation.mutate(params, {
                onSuccess: (response) => {
                    const hasErrorMessages = response.status === 'warning';
                    if (hasErrorMessages) {
                        setPartialErrorMessages(response.warnings);
                    }

                    queryClient.invalidateQueries('getRiskRegister').then(() => {
                        handleSnackBar('Upload success', 'success');
                        sendEventWithDimensions({
                            category: 'Risk Register',
                            action: 'Upload Risk CSV',
                            label: `file: ${file.name}, assignee: ${assignee?.value}, tags: ${JSON.stringify(tags)}`,
                        });
                        !hasErrorMessages && closeModal();
                    });
                },
                onError: (error) => {
                    const message = 'Upload Risk Items Failed';
                    DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
                    closeModal();
                },
            });
        }
    };

    const closeModal = () => {
        handleModalClose();
        form.reset();
        setPartialErrorMessages(null);
    };

    return (
        <CustomizedModalBase isModalOpen={isShowModal} handleModalClose={closeModal}>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(submitForm)}>
                    <div className={classes.uploadExcelModal}>
                        <div className={classes.title}>Importing Risk items via CSV input</div>

                        {partialErrorMessages ? (
                            <PartialContent closeModal={closeModal} partialSuccess={partialErrorMessages} />
                        ) : (
                            <FormContent closeModal={closeModal} />
                        )}
                    </div>
                </form>
            </FormProvider>
        </CustomizedModalBase>
    );
};
